import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import { NoteEditorType, UserRole } from '../../types';
import { getSchema } from './analytics-events.utils';
import {
    ProductAnalyticsSummaryType,
    SummaryContextData,
    UiEventName,
} from './analytics-types';
import { DocumentType } from './types';
import { getSegmentsContext, SegmentsProps } from './segments';

const getSummaryContextSchema = (
    data: SummaryContextData & { sessionId: string },
    trackClientData = false,
) => {
    switch (data.summaryType) {
        case ProductAnalyticsSummaryType.EntitySummary:
            // https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/named_entity/jsonschema
            return {
                schema: getSchema('named_entity', '2-0-0'),
                data: {
                    session_id: data.sessionId,
                    entity_id: data.namedEntity.entityId,
                    name: trackClientData
                        ? data.namedEntity.entityName
                        : undefined,
                    type: data.namedEntity.entityType,
                },
            };
        case ProductAnalyticsSummaryType.MedicalNote:
            // https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/progress_note/jsonschema
            return {
                schema: getSchema('progress_note'),
                data: {
                    session_id: data.sessionId,
                    uri: data.noteInsight.text.metadata.sourceSegmentId,
                    text: trackClientData
                        ? data.noteInsight.text.content
                        : undefined,
                },
            };
        case ProductAnalyticsSummaryType.ChapterGist: {
            // https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/gist_headline/jsonschema
            const uri = `chapters/${data.chapter.chapterId}/gist`;
            return {
                schema: getSchema('gist_headline'),
                data: {
                    session_id: data.sessionId,
                    chapter_id: data.chapter.chapterId,
                    uri,
                    text: trackClientData
                        ? data.chapter.gist.content
                        : undefined,
                },
            };
        }
        case ProductAnalyticsSummaryType.ChapterHeadline: {
            // https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/gist_headline/jsonschema
            const uri = `chapters/${data.chapter.chapterId}/headline`;
            return {
                schema: getSchema('gist_headline'),
                data: {
                    session_id: data.sessionId,
                    chapter_id: data.chapter.chapterId,
                    uri,
                    text: trackClientData
                        ? data.chapter.headline.content
                        : undefined,
                },
            };
        }
        case ProductAnalyticsSummaryType.ChapterSnippet: {
            // https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/summary/jsonschema
            const segment = data.chapter.snippets.find(
                (s) => s.id === data.segmentId,
            );

            // segmentId has format: "<uuid>-snippet-<index>"
            const index = segment?.id
                ? Number(segment.id.split('-snippet-')[1])
                : undefined;

            return {
                schema: getSchema('summary', '2-0-2'),
                data: {
                    session_id: data.sessionId,
                    summary_id: data.chapter.chapterId,
                    index,
                    summary: trackClientData ? segment?.content : undefined,
                },
            };
        }

        default:
            throw new Error('Invalid summary type');
    }
};

export enum EvaluateSummaryActionType {
    Downvote = 'Downvote',
    CancelDownvote = 'CancelDownvote',
}
type EvaluateSummaryProps = {
    sessionId: string;
    actionType: EvaluateSummaryActionType;
    trackClientData?: boolean;
} & SummaryContextData;

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_summary/jsonschema
export const evaluateSummary = (props: EvaluateSummaryProps) => {
    let type: string = props.summaryType;
    // note categories are strings for FE as they will be changing over time.
    if (props.summaryType === ProductAnalyticsSummaryType.MedicalNote) {
        type = props.noteCategory;
    }

    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_summary', '3-0-0'),
            data: {
                summary_type: type,
                action_type: props.actionType,
            },
        },
        context: [getSummaryContextSchema(props, props.trackClientData)],
    });
};

type UseSummaryProps = {
    sessionId: string;
    actionType: NoteEditorType | string;
    trackClientData?: boolean;
} & SummaryContextData;

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/use_summary/jsonschema
export const useSummary = (props: UseSummaryProps & SummaryContextData) => {
    let type: string = props.summaryType;
    // note categories are strings for FE as they will be changing over time.
    if (props.summaryType === ProductAnalyticsSummaryType.MedicalNote) {
        type = props.noteCategory;
    }

    trackSelfDescribingEvent({
        event: {
            schema: getSchema('use_summary', '3-0-0'),
            data: {
                summary_type: type,
                action_type: props.actionType,
            },
        },
        context: [getSummaryContextSchema(props, props.trackClientData)],
    });
};

type EvaluateVideoCallLegacyProps = {
    rating: number;
    issueTypes: Array<string>;
    issueDescription: string;
    sessionId: string;
    userRole: UserRole;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_video_call/jsonschema
export const evaluateVideoCallLegacy = (
    props: EvaluateVideoCallLegacyProps,
) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_video_call', '2-0-0'),
            data: {
                rating: props.rating,
                issue_types: props.issueTypes,
                issue_description: props.issueDescription,
                session_id: props.sessionId,
                user_role: props.userRole,
            },
        },
    });
};

type EvaluateVideoCallProps = {
    rating: number;
    issueTypes: Array<string>;
    issueDescription: string;
    uphealVideoCallId: string;
    userRole: UserRole;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_video_call/jsonschema
export const evaluateVideoCall = (props: EvaluateVideoCallProps) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_video_call', '3-0-0'),
            data: {
                rating: props.rating,
                issue_types: props.issueTypes,
                issue_description: props.issueDescription,
                upheal_video_call_id: props.uphealVideoCallId,
                user_role: props.userRole,
            },
        },
    });
};

export type UiEventProps = {
    uiEventName: UiEventName;
    location?: string | null;
    property?: string | null;
    action?: string | null;
    clientId?: string | null;
    sessionId?: string | null;
    tags?: Array<string>;
    isDemoContract?: boolean;
    extraProperties?: Record<string, unknown>;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/ui_event/jsonschema
export const uiEvent = (props: UiEventProps) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('ui_event', '7-0-0'),
            data: {
                ui_event_name: props.uiEventName?.substr(0, 250),
                location: props.location?.substr(0, 250),
                property: props.property?.substr(0, 250),
                action: props.action?.substr(0, 250),
                client_id: props.clientId,
                session_id: props.sessionId,
                tags: props.tags,
                is_demo_contract: props.isDemoContract ?? null,
                extra_properties: props.extraProperties ?? null,
            },
        },
    });
};

type DocumentEvaluationProps = {
    documentId: string;
    documentType: DocumentType;
    feedback?: string | null;
    rating?: number | null;
    issueOptions?: Array<string>;
    issueTypes?: Array<string>;
};

const getDocumentEvaluationContext = (props: DocumentEvaluationProps) => {
    return {
        schema: getSchema('document_evaluation'),
        data: {
            document_id: props.documentId,
            document_type: props.documentType,
            feedback: props.feedback || null,
            rating: props.rating,
            issue_options: props.issueOptions,
            issue_types: props.issueTypes,
        },
    };
};

type EvaluateNoteProps = DocumentEvaluationProps & {
    sections?: Array<{
        type: string;
    }>;
    userRole: UserRole;
    consentToAccessTranscript?: boolean;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_note/jsonschema
export const evaluateNote = (props: EvaluateNoteProps) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_note', '4-0-0'),
            data: {
                document_id: props.documentId,
                document_type: props.documentType,
                sections: props.sections,
                user_role: props.userRole,
                consent_to_access_transcript:
                    props.consentToAccessTranscript ?? true,
            },
        },
        context: [getDocumentEvaluationContext(props)],
    });
};

type ChatHistoryMessage = {
    role: 'user' | 'assistant';
    content: string;
};

type EvaluateChatProps = {
    contractId: string;
    conversationId: string;
    rating: number | null;
    issueOptions: string[] | null;
    issueTypes: string[] | null;
    feedback: string | null;
    consentToAccessConversation: boolean;
    chatHistory: ChatHistoryMessage[];
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_chat/jsonschema
export const evaluateChat = (props: EvaluateChatProps) => {
    if (props.rating === null) {
        return;
    }

    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_chat', '3-0-0'),
            data: {
                contract_id: props.contractId || null,
                conversation_id: props.conversationId,
                rating: props.rating,
                issue_options: props.issueOptions,
                issue_types: props.issueTypes,
                feedback: props.feedback,
                consent_to_access_conversation:
                    props.consentToAccessConversation,
                chat_history: props.chatHistory,
            },
        },
    });
};

type EvaluateSegmentsProps = DocumentEvaluationProps & {
    userRole: UserRole;
    sourceSegmentId: string;
    action: 'like' | 'dislike';
    segments?: SegmentsProps['segments'];
    consentToAccessTranscript?: boolean;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_segments/jsonschema
export const evaluateSegments = (props: EvaluateSegmentsProps) => {
    const context = [
        getDocumentEvaluationContext(props),
        props.segments
            ? getSegmentsContext({
                  documentId: props.documentId,
                  documentType: props.documentType,
                  segments: props.segments,
              })
            : null,
    ].filter(Boolean);

    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_segments', '2-0-0'),
            data: {
                document_id: props.documentId,
                document_type: props.documentType,
                user_role: props.userRole,
                source_segment_id: props.sourceSegmentId,
                action: props.action,
                consent_to_access_transcript:
                    props.consentToAccessTranscript ?? true,
            },
        },
        context,
    });
};

type ReportSingleVoiceWarningMistakeProps = {
    sessionId: string;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/report_single_voice_warning_mistake/jsonschema
export const reportSingleVoiceWarningMistake = (
    props: ReportSingleVoiceWarningMistakeProps,
) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('report_single_voice_warning_mistake'),
            data: {
                session_id: props.sessionId,
            },
        },
    });
};

export type EvaluationComplianceType = 'internal' | 'document';

type EvaluateComplianceCheckProps = {
    complianceCheckId: string;
    userRole: UserRole;
    feedback?: string;
    issueTypes?: Array<string>;
    action: 'like' | 'dislike';
    complianceType: EvaluationComplianceType;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_compliance_check/jsonschema/2-0-1
export const evaluateComplianceCheck = (
    props: EvaluateComplianceCheckProps,
) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_compliance_check', '2-0-1'),
            data: {
                compliance_check_id: props.complianceCheckId,
                user_role: props.userRole,
                action: props.action,
                feedback: props.feedback,
                issue_types: props.issueTypes,
                compliance_type: props.complianceType,
            },
        },
    });
};

type EvaluateComplianceCheckCriterionProps = {
    complianceCheckId: string;
    userRole: UserRole;
    feedback?: string;
    action: 'like' | 'dislike';
    criterionName: string;
    complianceType: EvaluationComplianceType;
};

// https://github.com/Upheal/upheal-analytics/tree/main/schemas/io.upheal/evaluate_compliance_check_criterion/1-0-0
export const evaluateComplianceCheckCriterion = (
    props: EvaluateComplianceCheckCriterionProps,
) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_compliance_check_criterion', '2-0-0'),
            data: {
                compliance_check_id: props.complianceCheckId,
                user_role: props.userRole,
                action: props.action,
                feedback: props.feedback,
                criterion_name: props.criterionName,
                compliance_type: props.complianceType,
            },
        },
    });
};

type EvaluateTemplateFromNoteProps = {
    templateId: string;
    action: string;
    feedback?: string | null;
    selectedSections?: Array<string> | null;
};

export const evaluateTemplateFromNote = (
    props: EvaluateTemplateFromNoteProps,
) => {
    trackSelfDescribingEvent({
        event: {
            schema: getSchema('evaluate_template_from_note'),
            data: {
                template_id: props.templateId,
                action: props.action,
                feedback: props.feedback ?? null,
                selected_sections: props.selectedSections ?? null,
            },
        },
    });
};
